import React, { useCallback, useEffect, useRef, useState } from "react";
import { getFileUrl, renewSignedFileUrl } from "../utils/fileUrlHelper";

export default function RenewableFileImage({
  src,
  fallbackSrc = "",
  loadTimeoutMs = 0,
  onLoad,
  onError,
  alt = "",
  ...props
}) {
  const [currentSrc, setCurrentSrc] = useState(() => getFileUrl(src));
  const stageRef = useRef("primary");
  const generationRef = useRef(0);
  const loadedRef = useRef(false);
  const timeoutRef = useRef(null);

  const clearLoadTimeout = useCallback(() => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  useEffect(() => {
    generationRef.current += 1;
    loadedRef.current = false;
    stageRef.current = "primary";
    clearLoadTimeout();

    const primaryUrl = getFileUrl(src);
    const fallbackUrl = getFileUrl(fallbackSrc);
    setCurrentSrc(primaryUrl);

    const timeoutMs = Number(loadTimeoutMs || 0);
    if (timeoutMs > 0 && fallbackUrl && fallbackUrl !== primaryUrl) {
      const generation = generationRef.current;
      timeoutRef.current = window.setTimeout(() => {
        if (generationRef.current !== generation || loadedRef.current) return;
        if (!String(stageRef.current).startsWith("primary")) return;
        generationRef.current += 1;
        stageRef.current = "fallback";
        setCurrentSrc(fallbackUrl);
      }, timeoutMs);
    }

    return clearLoadTimeout;
  }, [clearLoadTimeout, fallbackSrc, loadTimeoutMs, src]);

  const reportFinalError = (event) => {
    clearLoadTimeout();
    stageRef.current = "failed";
    onError?.(event);
  };

  const switchToFallback = (event) => {
    const fallbackUrl = getFileUrl(fallbackSrc);
    const primaryUrl = getFileUrl(src);
    if (!fallbackUrl || fallbackUrl === primaryUrl || fallbackUrl === currentSrc) {
      reportFinalError(event);
      return;
    }
    generationRef.current += 1;
    clearLoadTimeout();
    stageRef.current = "fallback";
    setCurrentSrc(fallbackUrl);
  };

  const handleError = async (event) => {
    const stage = stageRef.current;
    if (stage === "failed") return;

    if (stage === "primary-renewed") {
      switchToFallback(event);
      return;
    }

    if (stage === "fallback-renewed") {
      reportFinalError(event);
      return;
    }

    const sourceToRenew = stage === "fallback" ? fallbackSrc : src;
    if (!sourceToRenew) {
      if (stage === "fallback") reportFinalError(event);
      else switchToFallback(event);
      return;
    }

    const renewingStage = stage === "fallback" ? "fallback-renewing" : "primary-renewing";
    const renewedStage = stage === "fallback" ? "fallback-renewed" : "primary-renewed";
    const generation = generationRef.current;
    stageRef.current = renewingStage;

    try {
      const renewed = await renewSignedFileUrl(sourceToRenew);
      if (generationRef.current !== generation || loadedRef.current || stageRef.current !== renewingStage) return;
      if (!renewed || renewed === currentSrc) throw new Error("file_url_not_renewed");
      stageRef.current = renewedStage;
      setCurrentSrc(renewed);
    } catch (_) {
      if (generationRef.current !== generation || loadedRef.current || stageRef.current !== renewingStage) return;
      if (stage === "fallback") reportFinalError(event);
      else switchToFallback(event);
    }
  };

  const handleLoad = (event) => {
    loadedRef.current = true;
    clearLoadTimeout();
    onLoad?.(event);
  };

  return <img {...props} src={currentSrc || ""} alt={alt} onLoad={handleLoad} onError={handleError} />;
}
